// AuthContext.jsx
// G2 Phase C3 — authentication state, now backed by real Supabase Auth.
//
// Replaces the prior placeholder (env-var password + custom localStorage session).
// The outward contract is unchanged so App.jsx and LoginPage need no structural
// changes: { isAuthenticated, isLoading, session, step, login, verifyMfa, logout }.
// New additions: user, tenantId, role (decoded from the JWT), and getToken().
//
// Session persistence + refresh are handled by the Supabase client.

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { onAuthFailure, apiFetch } from '../lib/api';

const AuthContext = createContext(null);

// Step structure retained for a future MFA phase; Supabase login is single-step today.
export const AUTH_STEPS = {
  CREDENTIALS: 'credentials',
  MFA:         'mfa',
  DONE:        'done',
};

// Decode the JWT payload (NOT verified here — verification is the backend's job)
// to surface the tenant_id / user_role our access-token hook injects. This is also
// the end-to-end proof: once logged in, tenantId should be populated.
function decodeClaims(accessToken) {
  try {
    let payload = accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    while (payload.length % 4) payload += '=';
    const json = JSON.parse(atob(payload));
    return { tenantId: json.tenant_id || null, role: json.user_role || null };
  } catch {
    return { tenantId: null, role: null };
  }
}

function friendlyAuthError(msg) {
  if (/invalid login credentials/i.test(msg)) return 'Invalid email or password.';
  if (/email not confirmed/i.test(msg))      return 'Email not confirmed — check your inbox or contact support.';
  return msg || 'Sign in failed. Please try again.';
}

// Is this page load the return leg of an OAuth redirect?
//
// P0.22. The provider sends the session back in the URL fragment
// (#access_token=…&refresh_token=…), and detectSessionInUrl parses it
// ASYNCHRONOUSLY — then strips the fragment once it has been read. So this must be
// evaluated at module/mount time, before the first getSession() resolves; asking
// later returns false for a load that genuinely was an OAuth return.
//
// Why it matters: getSession() can resolve BEFORE the fragment is parsed and answer
// null. Treating that as "signed out" ended the loading state on a half-settled
// session, and AuthGate rendered a decision it had no business making yet — the login
// page, then a blank screen once the real session arrived. That is what Jim hit on his
// first Microsoft sign-in, 9 Sep 2026.
//
// Errors are swallowed: a browser that denies access to location must not take down
// the provider. Returning false degrades to the old behaviour rather than throwing.
function hasOAuthFragment() {
  try {
    const hash = String(window.location.hash || '');
    return hash.includes('access_token=') || hash.includes('error_description=');
  } catch {
    return false;
  }
}

export function AuthProvider({ children }) {
  const [session, setSession]     = useState(null);
  const [isLoading, setIsLoading] = useState(true);   // checking persisted session
  const [step, setStep]           = useState(AUTH_STEPS.CREDENTIALS);

  useEffect(() => {
    let mounted = true;

    // Captured once, at mount — see hasOAuthFragment(). By the time getSession()
    // resolves the fragment may already have been consumed and stripped.
    const awaitingOAuthSession = hasOAuthFragment();

    // Restore any persisted session on mount
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      const s = data?.session || null;
      setSession(s);
      setStep(s ? AUTH_STEPS.DONE : AUTH_STEPS.CREDENTIALS);

      // On an OAuth return, a null session here means the fragment has not been
      // parsed YET, not that the user is signed out. Stay loading and let
      // onAuthStateChange below end it — AuthGate renders null while loading, which
      // is the correct thing to show for the half-second the session is settling.
      //
      // The timeout is the backstop: if the fragment is malformed, or the provider
      // returned an error we never turn into a session, nothing else would ever clear
      // this and the app would hang on a blank screen — trading Jim's bug for a worse
      // one. 8s is far longer than a local parse needs and short enough to feel like a
      // failure rather than a freeze.
      if (s || !awaitingOAuthSession) {
        setIsLoading(false);
      } else {
        setTimeout(() => { if (mounted) setIsLoading(false); }, 8000);
      }
    });

    // Keep in sync with sign-in / sign-out / token refresh
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s || null);
      setStep(s ? AUTH_STEPS.DONE : AUTH_STEPS.CREDENTIALS);

      // P0.22 — this is what ends the OAuth return's loading state.
      //
      // On a first return getSession() above deliberately stays loading, because a
      // null session there means "the fragment has not been parsed yet". THIS is the
      // callback that fires when it has been. Without clearing it here the app would
      // sit blank until the 8s backstop — a worse bug than the one being fixed.
      //
      // Unconditional, including sign-out: any resolved auth state is a settled one,
      // and leaving isLoading true after a signOut would blank the login page.
      setIsLoading(false);
    });

    // B14 — react to a 401 from any /api call.
    //
    // Before the Phase 5 cutover nothing 401s: a request without a valid token
    // falls back to the legacy service-role context and returns data. After it,
    // an expired session would otherwise render an empty page — indistinguishable
    // from a tenant that genuinely has no rows.
    //
    // Verify the session before acting. A single 401 can also mean the backend
    // rejected a token that is still locally valid (a deactivated user, or a
    // profile row that lost its tenant), so refreshSession() is the arbiter: if
    // it fails, the session really is gone and signing out sends the user to the
    // login page. If it succeeds, the 401 was about that request, not the
    // session, and logging out would be wrong.
    const offAuthFailure = onAuthFailure(async () => {
      const { data, error } = await supabase.auth.refreshSession();
      if (error || !data?.session) {
        console.warn('[AuthContext] session could not be refreshed after a 401 — signing out');
        await supabase.auth.signOut();
        setSession(null);
        setStep(AUTH_STEPS.CREDENTIALS);
      }
    });

    return () => { mounted = false; sub?.subscription?.unsubscribe(); offAuthFailure(); };
  }, []);

  // LoginPage passes { username, password, rememberMe }; username holds the email.
  const login = useCallback(async ({ username, email, password }) => {
    const emailValue = (email || username || '').trim();
    if (!emailValue || !password) {
      return { success: false, error: 'Please enter your email and password.' };
    }
    const { data, error } = await supabase.auth.signInWithPassword({ email: emailValue, password });
    if (error) return { success: false, error: friendlyAuthError(error.message) };
    setSession(data.session);
    setStep(AUTH_STEPS.DONE);
    return { success: true };
  }, []);

  // ── OAuth sign-in (Google / Microsoft) ──────────────────────────────────
  //
  // Redirect flow, not popup: popups are blocked by default in several browsers and
  // fail silently when they are, which looks identical to a broken button.
  //
  // redirectTo is resolved from window.location.origin rather than hardcoded. The app
  // runs on www.mydigitalme.ai, on Vercel preview URLs, and on localhost during
  // development; a fixed URL would send a developer's sign-in to production. Every
  // origin used must also be listed in Supabase → Authentication → URL Configuration,
  // or the provider redirects to the site default and the session lands on the wrong
  // host.
  //
  // WHAT HAPPENS TO A BRAND-NEW USER. Supabase creates the auth.users row, the
  // on_auth_user_created trigger (migration 0003) creates a public.users row parked in
  // the holding tenant with active = false, and the access-token hook injects no
  // tenant claim. They are signed in and can see nothing — which is correct, and is why
  // `pendingApproval` below exists: without it that state is indistinguishable from a
  // broken product.
  const signInWithProvider = useCallback(async (provider) => {
    if (!['google', 'azure'].includes(provider)) {
      return { success: false, error: `Unsupported provider: ${provider}` };
    }
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: window.location.origin,
        // Microsoft needs an explicit scope request; Google's defaults already cover
        // profile and email. 'email' is what the trigger and every downstream display
        // name depend on, so it is not optional.
        ...(provider === 'azure' ? { scopes: 'email openid profile' } : {}),
      },
    });
    // On success the browser navigates away, so reaching this line means it failed.
    if (error) return { success: false, error: friendlyAuthError(error.message) };
    return { success: true };
  }, []);

  // ── Email sign-up (P0.13 task 13.10) ────────────────────────────────────
  //
  // There was no signUp() at all until now — only signInWithPassword and
  // signInWithOAuth — so "Get started" on the login page had nothing to call and fired
  // an alert() instead. A new user who typed an email and password therefore got
  // "Invalid email or password.", which states the opposite of their situation.
  //
  // full_name goes in options.data, which lands in raw_user_meta_data — EXACTLY where
  // Google and Microsoft put theirs. Migration 0020's trigger reads that one key
  // regardless of provider, so this path adds no second place for a name to live.
  //
  // What happens next is the same as OAuth: the trigger parks them in the holding
  // tenant, inactive, and PendingApprovalPage names the state. Sign-up is open;
  // ACCESS is not.
  const signUp = useCallback(async ({ email, password, fullName }) => {
    const emailValue = (email || '').trim();
    if (!emailValue || !password) {
      return { success: false, error: 'Please enter your email and a password.' };
    }
    const { data, error } = await supabase.auth.signUp({
      email: emailValue,
      password,
      options: {
        data: { full_name: (fullName || '').trim() || null },
        emailRedirectTo: window.location.origin,
      },
    });
    if (error) return { success: false, error: friendlyAuthError(error.message) };

    // With email confirmation ON, Supabase returns a user and NO session — the account
    // exists but cannot sign in until the link is clicked. With it OFF, a session comes
    // back and onAuthStateChange picks it up. Report which happened so the form can say
    // something true rather than guessing: telling someone to check their inbox when no
    // mail was sent is the same class of lie as the reset screen this release removes.
    return { success: true, needsConfirmation: !data?.session };
  }, []);

  // ── Password reset (P0.22) ──────────────────────────────────────────────
  //
  // The forgot-password screen used to await a 1200ms timer and then claim
  // unconditionally that "reset instructions have been sent". No mail was ever sent and
  // no Supabase call was ever made. That is worse than an unimplemented feature: the
  // user stops chasing it and waits for an email that cannot arrive.
  //
  // The neutral phrasing ("if an account exists") stays, and is now honest — it is also
  // what keeps this from being an account-enumeration oracle, so the caller must NOT
  // branch its message on the result.
  const requestPasswordReset = useCallback(async (email) => {
    const emailValue = (email || '').trim();
    if (!emailValue) return { success: false, error: 'Please enter your email address.' };
    const { error } = await supabase.auth.resetPasswordForEmail(emailValue, {
      redirectTo: window.location.origin,
    });
    if (error) return { success: false, error: friendlyAuthError(error.message) };
    return { success: true };
  }, []);

  // Placeholder retained so LoginPage's step router stays intact.
  const verifyMfa = useCallback(async () => {
    return { success: false, error: 'MFA not yet configured.' };
  }, []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setSession(null);
    setStep(AUTH_STEPS.CREDENTIALS);
  }, []);

  // Current access token, for authenticated API calls (see src/lib/api.js).
  const getToken = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  }, []);

  const isAuthenticated = !!session && step === AUTH_STEPS.DONE;
  const claims = session?.access_token ? decodeClaims(session.access_token) : { tenantId: null, role: null };

  // The tenant every new signup is parked in by migration 0003. It owns no data.
  //
  // This constant is load-bearing and was the source of a real bug: the original code
  // tested for the ABSENCE of a tenant claim, on the assumption that a pending user had
  // none. They do have one — the holding tenant is a genuine UUID, so the access-token
  // hook injects it like any other. A pending user therefore sailed through the gate and
  // was shown the dashboard, with every API call returning 401 behind it. Caught on the
  // first real pending sign-in, 29 Aug 2026.
  const HOLDING_TENANT = '00000000-0000-0000-0000-000000000000';

  // A signed-in user in the holding tenant, or with no tenant at all, is awaiting
  // approval.
  //
  // The access-token hook injects tenant_id ONLY when the public.users row has a
  // non-null tenant AND is active (migration 0003). A brand-new signup is parked in the
  // holding tenant, inactive, so the claim is absent and every RLS policy matches
  // nothing. Without naming that state the product renders as a working app with every
  // screen empty — the exact "empty screen, no error" failure CLAUDE.md warns about,
  // shown to the one person least able to interpret it.
  //
  // ── WHY THIS IS NOT DERIVED FROM THE CLAIM ALONE ───────────────────────────
  // A claim-only check would read "no tenant_id in this JWT" as "not approved", and
  // those are not the same thing. An EXISTING user holding a token minted before the
  // hook was registered has no tenant_id claim either — their account is fine, their
  // token is simply older than the hook. Gating on the claim alone would show the
  // pending screen to an established user and give them a Sign out button as the only
  // way forward.
  //
  // So the claim is treated as sufficient evidence of approval, never as evidence of
  // its absence. When the claim is missing we ask the server, which reads
  // public.users directly — the same source the hook reads, and the source of truth
  // either way. `null` means "not yet determined", and the pending screen is withheld
  // until the answer is actually known: a wrong answer here is worse than a slow one.
  const [profileApproved, setProfileApproved] = useState(null);
  const [adminsNotified, setAdminsNotified]   = useState(false);
  // Which of the TWO waits a pending user is in (13.12). Defaults to true so the
  // screen never claims "confirm your address" to somebody who has, or to an OAuth
  // user for whom the question does not apply — the server says otherwise only for an
  // email/password identity that has not clicked the link.
  const [emailConfirmed, setEmailConfirmed]   = useState(true);

  // True when the JWT itself says this user cannot have data: no claim, or the holding
  // tenant. Either way the server is asked to confirm before anything is shown.
  const claimSuggestsPending = !claims.tenantId || claims.tenantId === HOLDING_TENANT;

  useEffect(() => {
    // Only when signed in AND the claim indicates no real tenant. An established user
    // never triggers this, so the common path costs nothing.
    if (!isAuthenticated || !claimSuggestsPending) {
      setProfileApproved(null);
      return;
    }
    let cancelled = false;

    // P0.22 — this check used to run EXACTLY ONCE per mount, and that was the bug.
    //
    // It is the only moment the server learns someone has registered (B31), so a single
    // failed attempt meant no administrator was ever told and the screen sat blank. It
    // now retries: once now, again whenever the tab regains focus, and it never leaves
    // `profileApproved` at null indefinitely.
    //
    // Re-entrancy is handled by the server, not here. notifyAdminsOfPendingUser stamps
    // approval_notified_at after a confirmed send and returns 'already-notified'
    // thereafter, so repeated calls answer the same question without repeating the mail.
    const check = async () => {
      try {
        const res  = await apiFetch('/api/auth/approval-status');
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) { setProfileApproved(true); return; }
        setProfileApproved(Boolean(data.approved));
        setAdminsNotified(Boolean(data.administratorsNotified));
        // Absent on an older deploy, which must read as "confirmed" rather than
        // flipping every pending user onto the confirm-your-address screen.
        setEmailConfirmed(data.emailConfirmed !== false);
      } catch {
        // On error, assume approved. A failed lookup is not evidence of non-approval,
        // and the backend enforces access regardless — the worst case is an
        // approved-looking dashboard that renders empty, which is the status quo rather
        // than a new lockout.
        if (!cancelled) setProfileApproved(true);
      }
    };

    check();

    // Retry when the user comes back to the tab. This is what rescues the first OAuth
    // return: if the initial call raced a session that had not finished settling, the
    // next glance at the window repairs it without a manual re-click. It is also the
    // mechanism P0.13 task 13.2 needs — gating the notification on email_confirmed_at
    // is only safe if something calls again after the address is confirmed.
    const onFocus = () => { if (!cancelled && !document.hidden) check(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);

    // Never sit at null. `approvalPending` renders nothing, so a request that hangs —
    // rather than failing — would hold a blank screen for as long as the tab is open.
    // Resolving to `true` matches the catch above: the backend still enforces access,
    // so the worst case is an empty dashboard rather than a lockout.
    const settle = setTimeout(() => {
      if (!cancelled) setProfileApproved(prev => (prev === null ? true : prev));
    }, 10000);

    return () => {
      cancelled = true;
      clearTimeout(settle);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [isAuthenticated, claimSuggestsPending]);

  const pendingApproval = isAuthenticated && claimSuggestsPending && profileApproved === false;

  // True while the approval check is still in flight.
  //
  // `isLoading` covers restoring the session and nothing else. Between the session
  // resolving and the server answering, `profileApproved` is null — so `pendingApproval`
  // is false and the dashboard renders, fires its API calls, gets a wall of 401s, and is
  // then replaced by the pending screen. Observed as a visible flash of the command
  // centre on 29 Aug 2026.
  //
  // Only true for users whose claim already suggests they may be pending, so an
  // established user never waits on this.
  const approvalPending = isAuthenticated && claimSuggestsPending && profileApproved === null;

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      isLoading,
      session,
      user:     session?.user || null,
      tenantId: claims.tenantId,
      role:     claims.role,
      pendingApproval,
      approvalPending,
      adminsNotified,
      emailConfirmed,
      step,
      login,
      signUp,
      requestPasswordReset,
      signInWithProvider,
      verifyMfa,
      logout,
      getToken,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
